import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Bot, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AgentType,
  type AgentConfigDto,
  InquiryPublicDto,
  LlmChatSessionDto,
} from '@paladin-monorepo/common';
import { llmChatApi } from '@/lib/api/llm-chat';
import { useAuth } from '@/lib/auth';
import { isConsultantOrSolutionProvider } from '@/lib/permissions';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { LlmChatSessionSidebar } from '@/components/llm-chat/LlmChatSessionSidebar';
import { LlmChatWindow } from '@/components/llm-chat/LlmChatWindow';
import { NewLlmChatSessionDialog } from '@/components/llm-chat/NewLlmChatSessionDialog';
import { SelectLlmAgentTypePanel } from '@/components/llm-chat/SelectLlmAgentTypePanel';
import { useLlmChatDrawer } from '@/components/llm-chat/llm-chat-drawer-context';
import { useInquiries, useInquiry } from '@/hooks/useInquiry';
import { isInquiryOfferRoute } from '@/hooks/useLlmChatInquiryScope';
import { filterSelectableInquiriesForNewSession } from '@/lib/llm-chat-selectable-inquiries';
import {
  llmChatSessionsQueryKey,
  type LlmChatSessionsQueryFilters,
} from '@/lib/llm-chat-sessions-query';

export function LlmChatDrawer() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth({ tmp: 'LlmChatDrawer' });
  const isSupplier = isConsultantOrSolutionProvider(user);
  const [isPickingAgentType, setIsPickingAgentType] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [pendingSupplierAgentType, setPendingSupplierAgentType] = useState<AgentType | null>(null);
  const [filterByCurrentInquiry, setFilterByCurrentInquiry] = useState(true);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const {
    open,
    setOpen,
    activeSessionId,
    setActiveSessionId,
    pendingInitialMessage,
    setPendingInitialMessage,
    pendingInitialAttachmentIds,
    setPendingInitialAttachmentIds,
    agentQueryForNewSession,
    setAgentQueryForNewSession,
    scopeInquiryId,
    scopeConceptId,
  } = useLlmChatDrawer();

  const applyInquiryFilter = !!scopeInquiryId && filterByCurrentInquiry;
  const requiresInquirySelection = isSupplier && !scopeInquiryId;

  const { data: allInquiries = [], isLoading: inquiriesLoading } = useInquiries({
    enabled: open && requiresInquirySelection,
  });
  const selectableInquiries = useMemo(
    () => filterSelectableInquiriesForNewSession(allInquiries as InquiryPublicDto[]),
    [allInquiries],
  );
  const hasSelectableInquiries = selectableInquiries.length > 0;

  const sessionListFilters = useMemo((): LlmChatSessionsQueryFilters | undefined => {
    if (!applyInquiryFilter || !scopeInquiryId) return undefined;
    const filters: LlmChatSessionsQueryFilters = { inquiryId: scopeInquiryId };
    if (isSupplier && isInquiryOfferRoute(pathname)) {
      filters.agentType = AgentType.OfferAdvisor;
    }
    return filters;
  }, [applyInquiryFilter, scopeInquiryId, isSupplier, pathname]);

  const sessionsQueryKey = llmChatSessionsQueryKey(sessionListFilters);

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: sessionsQueryKey,
    queryFn: () => llmChatApi.getSessions(sessionListFilters),
    enabled: open,
  });

  const { data: scopedInquiry } = useInquiry(scopeInquiryId ?? undefined);

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => llmChatApi.deleteSession(sessionId),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] });
      queryClient.removeQueries({ queryKey: ['llm-chat-session', deletedId] });
      setActiveSessionId((prev) => (prev === deletedId ? null : prev));
    },
    onError: () => {
      toast.error(t('llmChat.deleteSessionError'));
    },
  });

  const clearHistoryMutation = useMutation({
    mutationFn: async (session: LlmChatSessionDto) => {
      await llmChatApi.deleteSession(session.id);
      queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] });
      queryClient.removeQueries({ queryKey: ['llm-chat-session', session.id] });
      const newSession = await llmChatApi.createSession({
        agentType: session.agentType,
        ...(session.inquiryId ? { inquiryId: session.inquiryId } : {}),
        ...(session.projectConceptId ? { projectConceptId: session.projectConceptId } : {}),
        provider: session.provider,
        model: session.model,
      });
      return newSession;
    },
    onSuccess: (newSession) => {
      void navigateToSession(newSession.id);
    },
    onError: () => {
      toast.error(t('llmChat.clearHistoryError'));
      setActiveSessionId(null);
    },
  });

  const { data: sessionData } = useQuery({
    queryKey: ['llm-chat-session', activeSessionId],
    queryFn: () => llmChatApi.getSession(activeSessionId!),
    enabled: open && !!activeSessionId,
  });

  const prevScopeRef = useRef(scopeInquiryId);
  useEffect(() => {
    if (prevScopeRef.current !== scopeInquiryId) {
      prevScopeRef.current = scopeInquiryId;
      setFilterByCurrentInquiry(true);
      setActiveSessionId(null);
    }
  }, [scopeInquiryId, setActiveSessionId]);

  useEffect(() => {
    if (!open || !activeSessionId || !applyInquiryFilter || !scopeInquiryId || !sessionData) return;
    if (sessionData.session.inquiryId !== scopeInquiryId) {
      setActiveSessionId(null);
    }
  }, [open, activeSessionId, applyInquiryFilter, scopeInquiryId, sessionData, setActiveSessionId]);

  const navigateToSession = useCallback(
    async (sessionId: string, agentType?: AgentType) => {
      const type = agentType ?? sessions.find((s) => s.id === sessionId)?.agentType;
      if (type === AgentType.DecisionAgent) {
        setOpen(false);
        navigate(`/llm-chat/${sessionId}`);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] });
      setActiveSessionId(sessionId);
    },
    [queryClient, setActiveSessionId, sessions, setOpen, navigate],
  );

  const handleSelectSession = useCallback(
    (id: string) => {
      setIsPickingAgentType(false);
      const type = sessions.find((s) => s.id === id)?.agentType;
      if (type === AgentType.DecisionAgent) {
        setOpen(false);
        navigate(`/llm-chat/${id}`);
        return;
      }
      setActiveSessionId(id);
    },
    [setActiveSessionId, sessions, setOpen, navigate],
  );

  const scopedDefaultAgentType = useMemo((): AgentType | null => {
    if (!scopeInquiryId) return null;
    return isSupplier ? AgentType.OfferAdvisor : AgentType.InquiryAdvisor;
  }, [scopeInquiryId, isSupplier]);

  const createSessionForInquiry = useCallback(
    async (agentType: AgentType, inquiryId: string) => {
      const session = await llmChatApi.createSession({ agentType, inquiryId });
      setAgentQueryForNewSession(null);
      setIsPickingAgentType(false);
      await navigateToSession(session.id);
    },
    [navigateToSession, setAgentQueryForNewSession],
  );

  const autoResumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoResumeAttemptedRef.current = false;
      return;
    }
    if (
      activeSessionId ||
      isPickingAgentType ||
      sessionsLoading ||
      isCreatingSession ||
      !applyInquiryFilter ||
      !scopeInquiryId ||
      autoResumeAttemptedRef.current
    ) {
      return;
    }

    autoResumeAttemptedRef.current = true;

    if (sessions.length > 0) {
      setActiveSessionId(sessions[0].id);
      return;
    }

    if (scopedDefaultAgentType) {
      setIsCreatingSession(true);
      void createSessionForInquiry(scopedDefaultAgentType, scopeInquiryId)
        .catch(() => toast.error(t('llmChat.createSessionError')))
        .finally(() => setIsCreatingSession(false));
    }
  }, [
    open,
    activeSessionId,
    isPickingAgentType,
    sessionsLoading,
    isCreatingSession,
    applyInquiryFilter,
    scopeInquiryId,
    sessions,
    scopedDefaultAgentType,
    createSessionForInquiry,
    setActiveSessionId,
    t,
  ]);

  const handleNewSession = useCallback(async () => {
    setActiveSessionId(null);
    if (scopeInquiryId && scopedDefaultAgentType) {
      setIsCreatingSession(true);
      try {
        await createSessionForInquiry(scopedDefaultAgentType, scopeInquiryId);
      } catch {
        toast.error(t('llmChat.createSessionError'));
      } finally {
        setIsCreatingSession(false);
      }
      return;
    }
    setIsPickingAgentType(true);
  }, [
    setActiveSessionId,
    scopeInquiryId,
    scopedDefaultAgentType,
    createSessionForInquiry,
    t,
  ]);

  const handleAgentTypeConfirmed = useCallback(
    async (config: AgentConfigDto) => {
      const { agentType } = config;
      if (scopeInquiryId) {
        await createSessionForInquiry(agentType, scopeInquiryId);
        return;
      }

      if (isSupplier) {
        if (config.requiresInquiry) {
          if (!hasSelectableInquiries) {
            toast.error(t('llmChat.noSelectableInquiryForAgent'));
            return;
          }
          setPendingSupplierAgentType(agentType);
          setIsPickingAgentType(false);
          setShowNewSession(true);
          return;
        }
        const session = await llmChatApi.createSession({ agentType });
        setAgentQueryForNewSession(null);
        setIsPickingAgentType(false);
        await navigateToSession(session.id, agentType);
        return;
      }
      const session = await llmChatApi.createSession({
        agentType,
        ...(scopeConceptId ? { projectConceptId: scopeConceptId } : {}),
      });
      setAgentQueryForNewSession(null);
      setIsPickingAgentType(false);
      await navigateToSession(session.id, agentType);
    },
    [
      isSupplier,
      scopeInquiryId,
      scopeConceptId,
      hasSelectableInquiries,
      createSessionForInquiry,
      navigateToSession,
      setAgentQueryForNewSession,
      t,
    ],
  );

  const handleCancelAgentPick = useCallback(() => {
    setIsPickingAgentType(false);
  }, []);

  const handleCreateSupplierSession = useCallback(
    async (inquiryId: string) => {
      const agentType = pendingSupplierAgentType ?? AgentType.OfferAdvisor;
      await createSessionForInquiry(agentType, inquiryId);
      setPendingSupplierAgentType(null);
    },
    [createSessionForInquiry, pendingSupplierAgentType],
  );

  const handleInitialConsumed = useCallback(() => {
    setPendingInitialMessage(undefined);
    setPendingInitialAttachmentIds(undefined);
  }, [setPendingInitialMessage, setPendingInitialAttachmentIds]);

  const handleSheetOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setIsPickingAgentType(false);
      setOpen(next);
    },
    [setOpen],
  );

  const handleDeleteSession = useCallback(
    (sessionId: string) => deleteSessionMutation.mutateAsync(sessionId),
    [deleteSessionMutation],
  );

  const handleClearHistory = useCallback(async () => {
    if (!sessionData?.session) return;
    await clearHistoryMutation.mutateAsync(sessionData.session);
  }, [sessionData?.session, clearHistoryMutation]);

  const deletingSessionId =
    deleteSessionMutation.isPending && deleteSessionMutation.variables !== undefined
      ? deleteSessionMutation.variables
      : null;

  const scopeInquiryTitle = scopedInquiry?.title ?? null;
  const showInquiryScopedNewSession = !!scopeInquiryId && filterByCurrentInquiry;

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent
        side="right"
        className="flex !w-[70vw] !max-w-[70vw] flex-col gap-0 overflow-hidden !p-0"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{t('llmChat.welcomeTitle')}</SheetTitle>
          <SheetDescription>{t('llmChat.welcomeDescription')}</SheetDescription>
        </SheetHeader>
        <div className="flex h-full min-h-0 flex-1 flex-col pt-10">
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <LlmChatSessionSidebar
              sessions={sessions}
              activeSessionId={activeSessionId ?? null}
              onSelectSession={handleSelectSession}
              onNewSession={() => void handleNewSession()}
              onDeleteSession={handleDeleteSession}
              deletingSessionId={deletingSessionId}
              isLoading={sessionsLoading}
              isCreatingSession={isCreatingSession}
              newSessionLabel={
                showInquiryScopedNewSession ? t('llmChat.newSessionForInquiry') : t('llmChat.newSession')
              }
              scopeInquiryId={scopeInquiryId}
              scopeInquiryTitle={scopeInquiryTitle}
              filterByCurrentInquiry={filterByCurrentInquiry}
              onFilterByCurrentInquiryChange={setFilterByCurrentInquiry}
            />

            {isPickingAgentType ? (
              <SelectLlmAgentTypePanel
                mode={isSupplier ? 'supplier' : 'inquirer'}
                initialAgentQuery={agentQueryForNewSession}
                unscopedSupplierFlow={requiresInquirySelection}
                hasSelectableInquiries={hasSelectableInquiries}
                inquiriesLoading={inquiriesLoading}
                onConfirm={handleAgentTypeConfirmed}
                onCancel={handleCancelAgentPick}
              />
            ) : sessionData ? (
              <LlmChatWindow
                session={sessionData.session}
                messages={sessionData.messages}
                initialMessage={pendingInitialMessage}
                initialAttachmentIds={pendingInitialAttachmentIds}
                onInitialMessageConsumed={handleInitialConsumed}
                onClearHistory={handleClearHistory}
                isClearingHistory={clearHistoryMutation.isPending}
              />
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-muted-foreground">
                <Bot className="h-12 w-12 shrink-0" />
                <div className="text-center">
                  <h3 className="text-lg font-medium">{t('llmChat.welcomeTitle')}</h3>
                  <p className="mt-1 text-sm">
                    {showInquiryScopedNewSession && scopeInquiryTitle
                      ? t('llmChat.sessionsForInquiry', { title: scopeInquiryTitle })
                      : t('llmChat.welcomeDescription')}
                  </p>
                </div>
                {showInquiryScopedNewSession && (
                  <Button
                    type="button"
                    onClick={() => void handleNewSession()}
                    disabled={isCreatingSession}
                  >
                    {isCreatingSession && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('llmChat.newSessionForInquiry')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {isSupplier && !scopeInquiryId && (
          <NewLlmChatSessionDialog
            open={showNewSession}
            onOpenChange={(next) => {
              setShowNewSession(next);
              if (!next) setPendingSupplierAgentType(null);
            }}
            onCreateSession={handleCreateSupplierSession}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
